import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App } from '@capacitor/app';
import packageJson from '../../../package.json';

export type ReleaseChannel = 'production' | 'beta' | 'alpha';

export const RELEASE_CHANNELS: readonly ReleaseChannel[] = ['production', 'beta', 'alpha'] as const;

export interface VersionManifest {
  latestVersion: string;
  channels?: Partial<Record<ReleaseChannel, string>>;
  androidStoreUrl?: string;
  androidBetaOptOutUrl?: string;
  iosStoreUrl?: string;
  minSupportedVersion?: string;
  debugLogEndpoint?: string;
}

export interface VersionCheckResult {
  installedVersion: string;
  latestVersion: string;
  hasUpdate: boolean;
  updateUrl?: string;
  betaOptOutUrl?: string;
  minSupportedVersion?: string;
  channel: ReleaseChannel;
}

const CHANNEL_STORAGE_KEY = 'aforo_release_channel';

@Injectable({
  providedIn: 'root'
})
export class AppVersionService {
  private http = inject(HttpClient);
  private readonly remoteManifestUrl = 'https://aforopro.bink.tools/version.json';

  readonly preferredChannel = signal<ReleaseChannel>(this.loadChannelPreference());

  async getInstalledVersion(): Promise<string> {
    try {
      const info = await App.getInfo();
      if (info?.version) {
        return info.version;
      }
    } catch {
      // Web / plugin unavailable — fall through to package version
    }
    return (packageJson as { version?: string }).version || '0.0.0';
  }

  async checkForUpdate(installedVersion: string): Promise<VersionCheckResult> {
    const manifest = await this.fetchManifest();
    const channel = this.getEffectiveChannel(installedVersion);
    const latestVersion = this.getLatestForChannel(manifest, channel);
    const hasUpdate = this.compareVersions(installedVersion, latestVersion) < 0;

    return {
      installedVersion,
      latestVersion,
      hasUpdate,
      updateUrl: this.getUpdateUrl(manifest),
      betaOptOutUrl: this.getBetaOptOutUrl(manifest, channel),
      minSupportedVersion: manifest.minSupportedVersion,
      channel
    };
  }

  getEffectiveChannel(installedVersion: string): ReleaseChannel {
    const preferred = this.preferredChannel();
    if (preferred !== 'production') {
      return preferred;
    }
    return this.detectChannel(installedVersion);
  }

  detectChannel(version: string): ReleaseChannel {
    const lower = (version || '').toLowerCase();
    if (lower.includes('alpha')) return 'alpha';
    if (lower.includes('beta')) return 'beta';
    return 'production';
  }

  setPreferredChannel(channel: ReleaseChannel): void {
    this.preferredChannel.set(channel);
    try {
      if (channel === 'production') {
        localStorage.removeItem(CHANNEL_STORAGE_KEY);
      } else {
        localStorage.setItem(CHANNEL_STORAGE_KEY, channel);
      }
    } catch {
      // Storage unavailable
    }
  }

  private loadChannelPreference(): ReleaseChannel {
    try {
      const stored = localStorage.getItem(CHANNEL_STORAGE_KEY);
      if (stored === 'beta' || stored === 'alpha') {
        return stored;
      }
    } catch {
      // Storage unavailable
    }
    return 'production';
  }

  private getLatestForChannel(manifest: VersionManifest, channel: ReleaseChannel): string {
    if (manifest.channels?.[channel]) {
      return manifest.channels[channel]!;
    }
    return manifest.latestVersion;
  }

  private async fetchManifest(): Promise<VersionManifest> {
    const candidates = this.getManifestCandidates();
    let lastError: unknown;

    for (const url of candidates) {
      try {
        const manifest = await this.httpGet(url);
        if (manifest?.latestVersion) {
          return manifest;
        }
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError ?? new Error('Unable to fetch version manifest');
  }

  private async httpGet(url: string): Promise<VersionManifest> {
    if (Capacitor.isNativePlatform()) {
      const response = await CapacitorHttp.get({ url, headers: { 'Accept': 'application/json' } });
      return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    }
    return firstValueFrom(this.http.get<VersionManifest>(url));
  }

  private getManifestCandidates(): string[] {
    if (Capacitor.getPlatform() === 'web') {
      return ['/version.json', this.remoteManifestUrl];
    }
    return [this.remoteManifestUrl];
  }

  private getUpdateUrl(manifest: VersionManifest): string | undefined {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return manifest.iosStoreUrl;
    }
    if (platform === 'android') {
      return manifest.androidStoreUrl;
    }
    return manifest.androidStoreUrl || manifest.iosStoreUrl;
  }

  private getBetaOptOutUrl(manifest: VersionManifest, channel: ReleaseChannel): string | undefined {
    if (channel !== 'production' || Capacitor.getPlatform() !== 'android') {
      return undefined;
    }
    return manifest.androidBetaOptOutUrl;
  }

  compareVersions(left: string, right: string): number {
    const leftParts = this.parseVersion(left);
    const rightParts = this.parseVersion(right);
    const maxLength = Math.max(leftParts.length, rightParts.length);

    for (let i = 0; i < maxLength; i++) {
      const l = leftParts[i] ?? 0;
      const r = rightParts[i] ?? 0;
      if (l > r) return 1;
      if (l < r) return -1;
    }
    return 0;
  }

  private parseVersion(version: string): number[] {
    const normalized = (version || '')
      .replace(/^[^\d]*/, '')
      .split('-')[0];

    return normalized
      .split('.')
      .map((part) => Number.parseInt(part, 10))
      .map((value) => (Number.isFinite(value) ? value : 0));
  }
}
